import React, { useState } from "react";
import { postData } from "../../../services/Axios/Axios";
import { CircleUserRound, CircleX } from "lucide-react";
import { ApiUrl } from "../../../config/apiConfig";
import { IJob } from "../../../models/Job";
import { showErrorToast, showSuccessToast } from "../../../utils/utils";
import PhoneInput from "react-phone-input-2";
import PrimaryButton from "../../UIElements/PrimaryButton";



type JobForm = Omit<IJob, "_id" | "createdAt" | "updatedAt">;

const inputClass =
  "w-full h-[32px] px-4 py-2 border placeholder:text-[13px] placeholder:text-gray-500 border-gray-400 mt-[2px] hover:border-gray-600 rounded-[5px] bg-gray-50 text-gray-800 font-normal focus:outline-none focus:ring-[1.5px] focus:ring-greenBg mb-1 focus:border-transparent transition-all duration-300 text-[13px]";
const selectClass =
  "w-full h-[32px] px-4 py-[3px] border leading-[20px] border-gray-400 mt-[2px] hover:border-gray-600 rounded-[5px] bg-gray-50 text-gray-800 font-normal focus:outline-none focus:ring-[1.5px] focus:ring-greenBg mb-1 focus:border-transparent transition-all duration-300 text-[13px]";

const JOB_CATEGORIES = [
  "Technology",
  "Sales",
  "Marketing",
  "HR",
  "Finance",
  "Operations",
  "Design",
  "Support",
  "Admin",
  "Other",
];

const initialState: JobForm = {
  businessName: "",
  location: "",
  jobTitle: "",
  contact: "",
  email: "",
  companyWebsite: "",
  applyLink: "",
  categoryId: "",
  linkedin: null,
  facebook: null,
  xcom: null,
  instagram: null,
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  categoryId: string;
}

const CreateJobModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, categoryId }) => {
  const [form, setForm] = useState<JobForm>(initialState);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value || null }));
  };

  const validateForm = (): boolean => {
    if (!form.businessName.trim()) {
      showErrorToast("Business Name is required");
      return false;
    }
    if (!form.jobTitle.trim()) {
      showErrorToast("Job Title is required");
      return false;
    }
    if (!form.location.trim()) {
      showErrorToast("Location is required");
      return false;
    }
    if (!form.contact.trim()) {
      showErrorToast("Contact is required");
      return false;
    }
    if (!form.email.trim()) {
      showErrorToast("Email is required");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      showErrorToast("Enter a valid email address");
      return false;
    }

    if (!form.applyLink.trim()) {
      showErrorToast("Apply Link is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      form.categoryId = categoryId;
      await postData(ApiUrl.createJob, form);
      onSuccess?.();
      setForm(initialState);
      onClose();
      showSuccessToast("Job added successfully!");
    } catch (e: any) {
      showErrorToast(e?.response?.data?.message ?? "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl animate-fadeIn max-h-[70vh] flex flex-col">
        {/* HEADER */}
        <div
          className="flex self-start gap-[10px] h-[45px] justify-between items-center text-white w-full 
          bg-gradient-to-r from-[#007070] via-[#06baba] to-[#007070] 
          bg-[length:200%_200%] animate-[gradient-x_4s_ease_infinite] 
          py-1 px-4 rounded-t-[8px] shadow-md"
        >
          <div className="flex items-center space-x-2">
            <CircleUserRound />
            <h2 className="text-lg font-semibold text-white tracking-wide">
              Add Job
            </h2>
          </div>
          <CircleX
            size={20}
            className="bg-white text-greenBg rounded-full hover:p-1 cursor-pointer transition-all duration-500 hover:rotate-180 active:rotate-0"
            onClick={onClose}
          />
        </div>

        {/* BODY */}
        <div className="px-4 py-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {/* Business Name */}
            <div>
              <label className="text-[#999999] text-[14px]">
                Business Name <span className="text-red-500">*</span>
              </label>
              <input
                name="businessName"
                value={form.businessName}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            {/* Job Title */}
            <div>
              <label className="text-[#999999] text-[14px]">
                Job Title <span className="text-red-500">*</span>
              </label>
              <input
                name="jobTitle"
                value={form.jobTitle}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            {/* Location */}
            <div>
              <label className="text-[#999999] text-[14px]">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                name="location"
                value={form.location}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            {/* Contact */}
            <div>
              <label className="text-[#999999] text-[14px]">
                Contact <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                value={form.contact}
                onlyCountries={["us"]}
                country={"us"}
                onChange={(v) => setForm((p) => ({ ...p, contact: v }))}
                disableDropdown
                inputClass={`w-full  px-4 py-[3px] border leading-[20px] border-gray-400 mt-[2px] hover:border-gray-600 rounded-[5px] bg-gray-50 text-gray-800 font-normal focus:outline-none focus:ring-[1.5px]  focus:ring-greenBg  mb-1 focus:border-transparent transition-all duration-300  text-[13px] overflow-hidden !h-[32px] !w-full`}
              />
            </div>

            {/* Email */}
            <div>
              <label className="text-[#999999] text-[14px]">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                name="email"
                value={form.email}
                onChange={handleChange}
                className={inputClass}
              />
            </div>



            {/* Company Website */}
            <div>
              <label className="text-[#999999] text-[14px]">
                Company Website
              </label>
              <input
                name="companyWebsite"
                value={form.companyWebsite ?? ""}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            {/* Apply Link */}
            <div>
              <label className="text-[#999999] text-[14px]">
                Apply Link <span className="text-red-500">*</span>
              </label>
              <input
                name="applyLink"
                value={form.applyLink}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            {/* Social Links */}
            <div>
              <label className="text-[#999999] text-[14px]">LinkedIn</label>
              <input
                name="linkedin"
                value={form.linkedin ?? ""}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div>
              <label className="text-[#999999] text-[14px]">Facebook</label>
              <input
                name="facebook"
                value={form.facebook ?? ""}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div>
              <label className="text-[#999999] text-[14px]">X (Twitter)</label>
              <input
                name="xcom"
                value={form.xcom ?? ""}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div>
              <label className="text-[#999999] text-[14px]">Instagram</label>
              <input
                name="instagram"
                value={form.instagram ?? ""}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            {/* Submit */}
            <div className="col-span-full flex justify-end">
              <PrimaryButton
                disabled={loading}
                onLoadingLabel="Saving"
                onClick={handleSubmit}
                label="Save Job"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateJobModal;